<template>
  <v-container fluid class="max-width-container px-1 px-md-6 py-1 py-md-4">
    <!-- Mobile Slide-In Overlay Drawer & Backdrop (< 960px) -->
    <teleport to="body">
      <template v-if="sidebar_open && isMobile">
        <div
          class="mobile-sidebar-backdrop"
          @click="sidebar_open = false"
        ></div>
        <div class="mobile-sidebar-drawer pa-4" style="position: relative; overflow: hidden;">
          <div class="acc-blob-gradient" />
          <AccessoriesSidebar
            :copier="copier"
            v-model:current-page="currentPage"
            :items-per-page="8"
            @toggle-dataset="$emit('toggle-dataset', $event)"
            @close="sidebar_open = false"
          />
        </div>
      </template>
    </teleport>

    <v-row v-if="!accessoriesFetching && copier.instance">
      <!-- 1. LEFT SIDEBAR FOR DESKTOP (>= 960px) -->
      <v-col v-if="sidebar_open && !isMobile" cols="12" md="3" class="transition-all">
        <v-card class="pa-4 border-card fill-height d-flex flex-column acc-card-wrapper" rounded="xl" variant="outlined" style="background: white; position: relative; overflow: hidden;">
          <div class="acc-blob-gradient" />
          <AccessoriesSidebar
            :copier="copier"
            v-model:current-page="currentPage"
            :items-per-page="8"
            @toggle-dataset="$emit('toggle-dataset', $event)"
            @close="sidebar_open = false"
          />
        </v-card>
      </v-col>

      <!-- 2. CENTER COLUMN: PRODUCT SHOWCASE & DIAGRAM (5 cols when sidebar open / 8 cols when collapsed) -->
      <v-col cols="12" :md="sidebar_open ? 5 : 8" class="transition-all">
        <v-card class="pa-6 border-card fill-height d-flex flex-column" rounded="xl" variant="outlined" style="background: white;">
          <!-- Header of diagram -->
          <div class="d-flex flex-column flex-md-row justify-space-between align-start mb-4 ga-3">
            <div class="product-header">
              <h1 class="product-title text-h4 mt-0 font-weight-black text-blue-grey-darken-4 mb-1">
                {{ copier.instance.name }}
              </h1>
              <div class="product-subtitle">
                <template v-for="(item, idx) in product_subtitle_items" :key="idx">
                  <span v-if="idx > 0" class="dot"></span>
                  <span>{{ item }}</span>
                </template>
              </div>
            </div>
            <div class="accessories-btn-wrapper d-flex align-center flex-shrink-0">
              <v-btn
                v-if="!sidebar_open"
                variant="flat"
                color="primary"
                size="small"
                rounded="pill"
                class="font-weight-bold text-caption accessories-btn"
                @click="sidebar_open = true"
              >
                <v-icon icon="mdi-plus-circle" class="mr-1.5" size="18" />
                &nbsp; Add Accessories
              </v-btn>
            </div>
          </div>

          <!-- Image stacks container -->
          <div class="diagram-container flex-grow-1 d-flex justify-center align-center position-relative mb-4">
            <ImageStacks :product="copier.instance" :attachments="datasetsAttached" />
          </div>

          <!-- Product Specs Tabs -->
          <div class="product-specs-tabs">
            <!-- Tabs Header for PRINTERS -->
            <div class="tabs-header" v-if="is_printer">
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'print' }"
                @click="active_specs_tab = 'print'"
              >
                <v-icon icon="mdi-printer" size="16" /> Print
              </button>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'scan' }"
                @click="active_specs_tab = 'scan'"
              >
                <v-icon icon="mdi-content-copy" size="16" /> Scan
              </button>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'paper' }"
                @click="active_specs_tab = 'paper'"
              >
                <v-icon icon="mdi-file-document-outline" size="16" /> Paper
              </button>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'network' }"
                @click="active_specs_tab = 'network'"
              >
                <v-icon icon="mdi-wifi" size="16" /> Network
              </button>
            </div>

            <!-- Tabs Header for NON-PRINTERS (Scanners, IP Phones, Others) -->
            <div class="tabs-header" v-else>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'overview' }"
                @click="active_specs_tab = 'overview'"
              >
                <v-icon icon="mdi-information-outline" size="16" /> Overview
              </button>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'specs' }"
                @click="active_specs_tab = 'specs'"
              >
                <v-icon icon="mdi-tune" size="16" /> Specifications
              </button>
              <button
                class="tab-btn"
                :class="{ active: active_specs_tab === 'connectivity' }"
                @click="active_specs_tab = 'connectivity'"
                v-if="has_connectivity"
              >
                <v-icon icon="mdi-wifi" size="16" /> Connectivity
              </button>
            </div>

            <!-- PRINTER TABS -->
            <template v-if="is_printer">
              <!-- Print Tab -->
              <div v-show="active_specs_tab === 'print'" class="tab-content">
                <div class="spec-row" v-if="copier.instance.print_technology || copier.instance.printer_category">
                  <div class="spec-key">Print Technology</div>
                  <div class="spec-val">{{ copier.instance.print_technology || copier.instance.printer_category || 'Multifunction Laser' }}</div>
                </div>
                <div class="spec-row" v-if="Number(copier.instance.speed_black) > 0">
                  <div class="spec-key">Speed (Mono)</div>
                  <div class="spec-val">Up to {{ copier.instance.speed_black }} ppm</div>
                </div>
                <div class="spec-row" v-if="Number(copier.instance.speed_color) > 0">
                  <div class="spec-key">Speed (Color)</div>
                  <div class="spec-val">Up to {{ copier.instance.speed_color }} ppm</div>
                </div>
                <div class="spec-row" v-if="copier.instance.duplex_printing && copier.instance.duplex_printing !== 'N/A'">
                  <div class="spec-key">Duplex Printing</div>
                  <div class="spec-val">{{ copier.instance.duplex_printing }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.model_number">
                  <div class="spec-key">Model Number</div>
                  <div class="spec-val">{{ copier.instance.model_number }}</div>
                </div>
              </div>

              <!-- Scan Tab -->
              <div v-show="active_specs_tab === 'scan'" class="tab-content">
                <div class="spec-row" v-if="copier.instance.capability && copier.instance.capability !== 'N/A'">
                  <div class="spec-key">Capability</div>
                  <div class="spec-val">{{ copier.instance.capability }}</div>
                </div>
                <div class="spec-row" v-if="Number(copier.instance.speed_black) > 0">
                  <div class="spec-key">Copy Speed</div>
                  <div class="spec-val">Up to {{ copier.instance.speed_black }} ppm</div>
                </div>
                <div class="spec-row" v-if="copier.instance.form_factor && copier.instance.form_factor !== '-'">
                  <div class="spec-key">Form Factor</div>
                  <div class="spec-val">{{ copier.instance.form_factor }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.duplex_printing && copier.instance.duplex_printing !== 'N/A'">
                  <div class="spec-key">Duplex Scan</div>
                  <div class="spec-val">Automatic (DADF)</div>
                </div>
              </div>

              <!-- Paper Tab -->
              <div v-show="active_specs_tab === 'paper'" class="tab-content">
                <div class="spec-row" v-if="Number(copier.instance.paper_capacity) > 0">
                  <div class="spec-key">Capacity</div>
                  <div class="spec-val">{{ copier.instance.paper_capacity }} sheets</div>
                </div>
                <div class="spec-row" v-if="copier.instance.paper_size && copier.instance.paper_size !== 'N/A' && !copier.instance.paper_size.includes('N/A')">
                  <div class="spec-key">Paper Size</div>
                  <div class="spec-val">{{ Array.isArray(copier.instance.paper_size) ? copier.instance.paper_size.join(', ') : copier.instance.paper_size }}</div>
                </div>
                <div class="spec-row" v-if="Number(copier.instance.prints_per_month) > 0">
                  <div class="spec-key">Standard Volume</div>
                  <div class="spec-val">{{ Number(copier.instance.prints_per_month).toLocaleString() }} pages</div>
                </div>
                <div class="spec-row" v-if="copier.instance.usage_class && copier.instance.usage_class !== 'N/A'">
                  <div class="spec-key">Usage Class</div>
                  <div class="spec-val">{{ copier.instance.usage_class }}</div>
                </div>
              </div>

              <!-- Network Tab -->
              <div v-show="active_specs_tab === 'network'" class="tab-content">
                <div class="spec-row" v-if="copier.instance.connectivity && copier.instance.connectivity.length">
                  <div class="spec-key">Connectivity</div>
                  <div class="spec-val">{{ Array.isArray(copier.instance.connectivity) ? copier.instance.connectivity.join(', ') : copier.instance.connectivity }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.display_size && copier.instance.display_size !== '0'">
                  <div class="spec-key">Display</div>
                  <div class="spec-val">{{ copier.instance.display_size }} touchscreen</div>
                </div>
                <div class="spec-row" v-if="copier.instance.mono_cpp || copier.instance.color_cpp">
                  <div class="spec-key">Page Cost (Mono / Color)</div>
                  <div class="spec-val">{{ copier.instance.mono_cpp ? '$' + copier.instance.mono_cpp : 'Included' }} / {{ copier.instance.color_cpp ? '$' + copier.instance.color_cpp : 'Included' }}</div>
                </div>
              </div>
            </template>

            <!-- NON-PRINTER TABS (Overview, Specs, Connectivity) -->
            <template v-else>
              <!-- Overview Tab -->
              <div v-show="active_specs_tab === 'overview'" class="tab-content">
                <p class="text-body-2 text-slate-700 line-height-relaxed ma-0 mb-3">
                  {{ copier.instance.short_description || copier.instance.description || 'Professional grade equipment designed for reliable enterprise communication and productivity.' }}
                </p>
                <div v-if="copier.instance.ai_best_fit" class="ai-highlights">
                  <div class="text-caption font-weight-black text-blue-darken-3 mb-1 d-flex align-center">
                    <v-icon icon="mdi-star-check" color="blue-darken-2" size="18" class="mr-1" />
                    KEY HIGHLIGHTS &amp; BEST FIT
                  </div>
                  <div class="ai-content" v-html="copier.instance.ai_best_fit"></div>
                </div>
              </div>

              <!-- Specifications Tab -->
              <div v-show="active_specs_tab === 'specs'" class="tab-content">
                <div class="spec-row" v-if="copier.instance.model_number">
                  <div class="spec-key">Model Number</div>
                  <div class="spec-val">{{ copier.instance.model_number }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.printer_category">
                  <div class="spec-key">Equipment Category</div>
                  <div class="spec-val">{{ copier.instance.printer_category }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.display_size && copier.instance.display_size !== '0'">
                  <div class="spec-key">Display Screen</div>
                  <div class="spec-val">{{ copier.instance.display_size }} touchscreen</div>
                </div>
                <div class="spec-row" v-if="copier.instance.scanner_type && copier.instance.scanner_type !== 'N/A'">
                  <div class="spec-key">Scanner Type</div>
                  <div class="spec-val">{{ copier.instance.scanner_type }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.scan_destinations">
                  <div class="spec-key">Destinations</div>
                  <div class="spec-val">{{ copier.instance.scan_destinations }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.dimensions">
                  <div class="spec-key">Dimensions</div>
                  <div class="spec-val">{{ copier.instance.dimensions }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.form_factor && copier.instance.form_factor !== '-'">
                  <div class="spec-key">Form Factor</div>
                  <div class="spec-val">{{ copier.instance.form_factor }}</div>
                </div>
              </div>

              <!-- Connectivity Tab -->
              <div v-show="active_specs_tab === 'connectivity'" class="tab-content">
                <div class="spec-row" v-if="copier.instance.connectivity && copier.instance.connectivity.length">
                  <div class="spec-key">Interface / Connections</div>
                  <div class="spec-val">{{ Array.isArray(copier.instance.connectivity) ? copier.instance.connectivity.join(', ') : copier.instance.connectivity }}</div>
                </div>
                <div class="spec-row" v-if="copier.instance.display_size && copier.instance.display_size !== '0'">
                  <div class="spec-key">Display Interface</div>
                  <div class="spec-val">{{ copier.instance.display_size }} touchscreen</div>
                </div>
              </div>
            </template>
          </div>
        </v-card>
      </v-col>

      <!-- 3. RIGHT COLUMN: OPTIONS & CONFIGURATION (4 cols) -->
      <v-col cols="12" md="4">
        <v-card class="pa-6 border-card shadow-quote fill-height d-flex flex-column" rounded="xl" variant="outlined" style="background: white;">
          <!-- Overline Tag / Inquiry Pill -->
          <div class="mb-3">
            <span class="inquiry-pill text-caption font-weight-black text-blue-grey-darken-3 px-3 py-1 rounded-pill d-inline-block">
              <span v-if="!is_quotable">EQUIPMENT INQUIRY</span>
              <span v-else-if="is_unpriced">CUSTOM QUOTE INQUIRY</span>
              <span v-else-if="ConfigShowPricing && show_monthly_price">YOUR ESTIMATED MONTHLY PRICE</span>
              <span v-else-if="ConfigShowPricing && !show_monthly_price">PRICING INQUIRY</span>
              <span v-else>SELECTED CONFIGURATION</span>
            </span>
          </div>

          <!-- Green qualification badges -->
          <div class="safe-badge-container d-none d-md-flex align-center pa-3 rounded-lg mb-4" style="gap: 12px;">
            <v-icon icon="mdi-shield-check" color="teal-darken-3" size="22" class="flex-shrink-0" />
            <div class="text-caption font-weight-bold text-green-darken-4 flex-grow-1" style="line-height: 1.4;" v-if="is_quotable && form.acquisition_type === 'lease'">
              No obligation &bull; Fast specialist proposal &amp; consultation
            </div>
            <div class="text-caption font-weight-bold text-green-darken-4 flex-grow-1" style="line-height: 1.4;" v-else-if="is_quotable">
              Service contract optional<span v-if="free_shipping"> &bull; Free delivery &amp; installation</span>
            </div>
            <div class="text-caption font-weight-bold text-green-darken-4 flex-grow-1" style="line-height: 1.4;" v-else>
              Custom commercial configuration<span v-if="free_shipping"> &bull; Free delivery</span>
            </div>
          </div>

          <!-- Price Display (Visible when ConfigShowPricing is true) -->
          <div class="mb-4" v-if="ConfigShowPricing">
            <div class="d-flex align-baseline gap-1 mb-1">
              <template v-if="show_monthly_price">
                <span class="price-val text-h3 font-weight-black text-blue-grey-darken-4 d-inline-flex align-center">
                  $<Odometer :value="computedMonthlyPrice || 0" />
                </span>
                <span class="text-body-1 font-weight-bold text-grey-darken-1">
                  /mo
                </span>
              </template>
              <span v-else-if="form.acquisition_type === 'purchase' || is_unpriced" class="pricing-request-text text-blue-grey-darken-4">
                {{ is_unpriced ? 'Custom pricing on request' : 'Purchase quote on request' }}
              </span>
              <span v-else class="pricing-request-text text-blue-grey-darken-4">
                Pricing upon request
              </span>
            </div>
            <p v-if="show_monthly_price" class="pricing-disclaimer-note mt-2 mb-0">
              <i>Displayed prices and monthly lease payments are estimates for informational purposes. Final pricing, availability, lease terms, taxes, delivery, installation, and service arrangements are determined by the participating dealer and applicable financing provider. Financing is subject to approval.</i>
            </p>
          </div>

          <!-- Configuration Spec Overview -->
          <div class="mb-4">
            <div class="text-h6 font-weight-black text-blue-grey-darken-4 mb-1">
              {{ is_quotable ? (form.acquisition_type === 'lease' ? 'Lease Configuration' : 'Purchase Configuration') : 'Equipment Details' }}
            </div>
            <div class="text-caption mt-0 text-grey-darken-1" v-if="is_quotable">
              <span v-if="form.acquisition_type === 'lease'">
                Includes service, toner &amp; delivery &bull; {{ form.lease_term }}-mo lease
              </span>
              <span v-else>
                Outright equipment purchase &bull; Maintenance plans available<span v-if="free_shipping"> &bull; Free delivery</span>
              </span>
            </div>
            <div class="text-caption mt-0 text-grey-darken-1" v-else>
              Custom commercial configuration<span v-if="free_shipping"> &bull; Free delivery</span>
            </div>
          </div>

          <!-- Acquisition type, Lease Term, and Volume Sliders (Printers Only) -->
          <template v-if="is_printer">
            <!-- Acquisition type toggle -->
            <div class="mb-6">
              <span class="text-overline font-weight-black text-grey-darken-2 d-block mb-2">ACQUISITION</span>
              <v-btn-toggle v-model="form.acquisition_type" mandatory rounded="lg" color="primary" class="acq-toggle">
                <v-btn value="lease" class="text-none font-weight-bold px-6">Lease</v-btn>
                <v-btn value="purchase" class="text-none font-weight-bold px-6">Purchase</v-btn>
              </v-btn-toggle>
            </div>

            <!-- Lease Term selection -->
            <div class="mb-6" v-if="form.acquisition_type === 'lease'">
              <span class="text-overline font-weight-black text-grey-darken-2 d-block mb-2">LEASE TERM</span>
              <v-btn-toggle v-model="form.lease_term" mandatory rounded="lg" color="primary" class="term-toggle d-none d-md-flex">
                <v-btn v-for="t in [12, 24, 36, 48, 60]" :key="t" :value="t" class="text-none font-weight-bold">
                  {{ t }} Months
                </v-btn>
              </v-btn-toggle>
              <div :class="['custom-outlined-dropdown d-md-none w-100', { 'dropdown-open': leaseDropdownOpen }]" v-click-outside="() => leaseDropdownOpen = false">
                <div class="dropdown-trigger" @click="leaseDropdownOpen = !leaseDropdownOpen">
                  <span>{{ activeLeaseTitle }}</span>
                  <v-icon :icon="leaseDropdownOpen ? 'mdi-menu-up' : 'mdi-menu-down'" size="20" class="ml-1" />
                </div>
                <transition name="dropdown-fade">
                  <div v-show="leaseDropdownOpen" class="dropdown-menu">
                    <div
                      v-for="t in [12, 24, 36, 48, 60]"
                      :key="t"
                      :class="['dropdown-item', { active: form.lease_term === t }]"
                      @click="form.lease_term = t; leaseDropdownOpen = false"
                    >
                      {{ t }} Months
                    </div>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Volume sliders (Lease Only) -->
            <div v-if="form.acquisition_type === 'lease'">
              <div class="mb-3">
                <div class="d-flex align-center justify-space-between">
                  <span class="text-overline font-weight-black text-grey-darken-2">B&W VOLUME</span>
                  <span class="text-body-2 font-weight-black text-blue-grey-darken-4">{{ copier.instance.prints_per_month?.toLocaleString() }} pages</span>
                </div>
                <v-slider
                  v-model="copier.instance.prints_per_month"
                  min="500"
                  max="20000"
                  step="500"
                  hide-details
                  color="primary"
                  track-color="blue-grey-lighten-4"
                />
              </div>

              <div class="mb-6" v-if="copier.instance.copier_color !== 'B&W'">
                <div class="d-flex align-center justify-space-between">
                  <span class="text-overline font-weight-black text-grey-darken-2">COLOR VOLUME</span>
                  <span class="text-body-2 font-weight-black text-blue-grey-darken-4">{{ copier.instance.color_volume?.toLocaleString() }} pages</span>
                </div>
                <v-slider
                  v-model="copier.instance.color_volume"
                  min="100"
                  max="10000"
                  step="100"
                  hide-details
                  color="orange-darken-2"
                  track-color="blue-grey-lighten-4"
                />
              </div>
            </div>
          </template>

          <!-- Features list -->
          <div class="mb-6 features-list" v-if="has_features_list">
            <span class="text-overline font-weight-black text-grey-darken-2 d-block mb-3">Features and connectivity</span>

            <div class="d-flex align-center justify-space-between py-1" v-if="copier.instance.display_size && copier.instance.display_size !== '0'">
              <span class="text-body-2 text-grey-darken-3">✓ {{ copier.instance.display_size }} touchscreen</span>
              <span class="text-caption text-green-darken-3 bg-green-lighten-5 px-2 py-0.5 rounded">Included</span>
            </div>

            <div class="d-flex align-center justify-space-between py-1" v-if="has_connectivity">
              <span class="text-body-2 text-grey-darken-3">✓ {{ Array.isArray(copier.instance.connectivity) ? copier.instance.connectivity.join(', ') : copier.instance.connectivity }}</span>
              <span class="text-caption text-green-darken-3 bg-green-lighten-5 px-2 py-0.5 rounded">Included</span>
            </div>

            <div class="d-flex align-center justify-space-between py-1" v-if="Number(copier.instance.paper_capacity) > 0">
              <span class="text-body-2 text-grey-darken-3">✓ {{ copier.instance.paper_capacity }} sheet max capacity</span>
              <span class="text-caption text-green-darken-3 bg-green-lighten-5 px-2 py-0.5 rounded">Included</span>
            </div>

            <div class="d-flex align-center justify-space-between py-1" v-if="copier.instance.duplex_printing && copier.instance.duplex_printing !== 'N/A' && copier.instance.duplex_printing !== '-'">
              <span class="text-body-2 text-grey-darken-3">✓ {{ copier.instance.duplex_printing }}</span>
              <span class="text-caption text-green-darken-3 bg-green-lighten-5 px-2 py-0.5 rounded">Included</span>
            </div>

            <div class="d-flex align-center justify-space-between py-1" v-if="copier.instance.scanner_type && copier.instance.scanner_type !== 'N/A' && copier.instance.scanner_type !== '-'">
              <span class="text-body-2 text-grey-darken-3">✓ {{ copier.instance.scanner_type }}</span>
              <span class="text-caption text-green-darken-3 bg-green-lighten-5 px-2 py-0.5 rounded">Included</span>
            </div>
          </div>

          <!-- Continue Button -->
          <v-btn
            :color="ConfigElementsColor"
            block
            rounded="lg"
            size="large"
            elevation="0"
            style="max-height:50px"
            class="text-none font-weight-bold"
            @click="$emit('proceed')"
          >
            {{ !is_quotable || is_unpriced ? 'Request Custom Quote' : (ConfigShowPricing && show_monthly_price ? 'Continue to my price' : 'Continue to installation details') }}
          </v-btn>

          <div class="mt-5 text-center text-caption text-grey-darken-2 font-italic" v-if="is_printer && ConfigShowPricing && show_monthly_price">
            *Final monthly payment is subject to change based on credit review and financing approval.
          </div>
          <div class="mt-5 text-center text-caption font-weight-bold text-grey-darken-1" v-else>
            No obligation &bull; Fast specialist proposal
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading Spinner -->
    <div v-else class="d-flex flex-column align-center justify-center py-16 fill-height w-100">
      <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-4" />
      <span class="text-body-1 font-weight-bold text-grey-darken-1">Loading machine options...</span>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import ImageStacks from "@/components/configurator/ImageStacks.vue"
import AccessoriesSidebar from "./AccessoriesSidebar.vue"
import Odometer from "./Odometer.vue"
import { IsPrinterCategory, IsProductCustomQuote, IsProductQuotable } from '@/mixins/PricingMixins.ts'

const props = defineProps<{
  copier: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  accessoriesFetching: boolean
  computedMonthlyPrice?: number
}>()

defineEmits<{
  (e: 'toggle-dataset', ds: any): void
  (e: 'proceed'): void
}>()

const store = useGlobalsStore()
const { form, free_shipping, ConfigElementsColor, ConfigShowPricing } = storeToRefs(store)

const sidebar_open = ref(window.innerWidth >= 960)
const leaseDropdownOpen = ref(false)
const isMobile = ref(window.innerWidth < 960)
const active_specs_tab = ref<string>('print')

// Pagination logic
const currentPage = ref(1)

function HandleResize() {
  isMobile.value = window.innerWidth < 960
}

const is_printer = computed(() => {
  return IsPrinterCategory(props.copier.instance)
})

const is_unpriced = computed(() => {
  return IsProductCustomQuote(props.copier.instance)
})

const is_quotable = computed(() => {
  return IsProductQuotable(props.copier.instance)
})

const show_monthly_price = computed(() => {
  return Boolean(ConfigShowPricing?.value) && !is_unpriced.value && is_quotable.value && form.value.acquisition_type === 'lease' && Number(props.computedMonthlyPrice || 0) > 0
})

const has_connectivity = computed(() => {
  const conn = props.copier.instance?.connectivity
  return Array.isArray(conn) ? conn.length > 0 : Boolean(conn && conn !== 'N/A' && conn !== '-')
})

const has_features_list = computed(() => {
  if (!props.copier?.instance) return false
  const inst = props.copier.instance
  const has_display = Boolean(inst.display_size && inst.display_size !== '0' && inst.display_size !== 'N/A' && inst.display_size !== '-')
  const has_conn = has_connectivity.value
  const has_paper = Number(inst.paper_capacity) > 0
  const has_duplex = Boolean(inst.duplex_printing && inst.duplex_printing !== 'N/A' && inst.duplex_printing !== '-')
  const has_scanner = Boolean(inst.scanner_type && inst.scanner_type !== 'N/A' && inst.scanner_type !== '-')
  return has_display || has_conn || has_paper || has_duplex || has_scanner
})

const product_subtitle_items = computed(() => {
  if (!props.copier.instance) return []
  const inst = props.copier.instance
  const items: string[] = []

  items.push(inst.printer_category || 'EQUIPMENT')

  if (is_printer.value) {
    if (inst.copier_color && inst.copier_color !== 'N/A' && inst.copier_color !== '-') {
      items.push(inst.copier_color.toUpperCase())
    }
    const speed = Number(inst.speed_black || inst.speed_color || 0)
    if (speed > 0) {
      items.push(`${speed} PPM`)
    }
    if (inst.form_factor && inst.form_factor !== '-' && inst.form_factor !== 'N/A') {
      items.push(inst.form_factor.toUpperCase())
    }
  } else {
    if (inst.model_number && inst.model_number !== '-' && inst.model_number !== 'N/A') {
      items.push(inst.model_number)
    }
    if (inst.display_size && inst.display_size !== '0' && inst.display_size !== 'N/A') {
      items.push(inst.display_size.toUpperCase())
    }
    if (inst.scanner_type && inst.scanner_type !== '-' && inst.scanner_type !== 'N/A') {
      items.push(inst.scanner_type.toUpperCase())
    }
  }

  return items
})

const activeLeaseTitle = computed(() => {
  return form.value.lease_term ? `${form.value.lease_term} Months` : 'Select Term'
})

const datasetsAttached = computed(() => {
  if (!props.copier.instance) return []
  return props.copier.datasets.filter((dataset: any) => dataset.selected)
})

watch(() => props.copier.instance?.id, () => {
  currentPage.value = 1
  if (!is_printer.value) {
    active_specs_tab.value = 'overview'
  } else {
    active_specs_tab.value = 'print'
  }
}, { immediate: true })

onMounted(() => {
  HandleResize()
  window.addEventListener('resize', HandleResize)
  if (window.innerWidth < 960) {
    sidebar_open.value = false
  }
  if (!is_printer.value) {
    active_specs_tab.value = 'overview'
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', HandleResize)
})
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1600px !important;
  margin: 0 auto !important;
  width: 100% !important;
}
.accessories-btn-wrapper {
  width: 100%;
  justify-content: center;
}
.accessories-btn {
  max-width: 220px;
}
@media (min-width: 960px) {
  .accessories-btn-wrapper {
    width: auto;
  }
  .accessories-btn {
    width: auto;
  }
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.badge-added {
  background-color: #e6f4ea !important;
  color: #137333 !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}
.diagram-container {
  background-color: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  min-height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.accessories-sidebar-list {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

.mobile-sidebar-drawer {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  bottom: 0 !important;
  width: 330px !important;
  max-width: 88vw !important;
  height: 100vh !important;
  z-index: 999999 !important;
  background: white !important;
  box-shadow: 10px 0 30px rgba(15, 23, 42, 0.25) !important;
  border-radius: 0 20px 20px 0 !important;
  animation: slideInLeft 0.28s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.mobile-sidebar-backdrop {
  position: fixed !important;
  inset: 0 !important;
  background: rgba(15, 23, 42, 0.45) !important;
  backdrop-filter: blur(2px) !important;
  z-index: 999998 !important;
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}
.addon-option-card {
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  border-radius: 12px !important;
  padding: 10px 14px !important;
  margin-bottom: 8px !important;

  &:hover {
    border-color: #cbd5e1 !important;
    box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.06);

    .action-circle.circle-add {
      background-color: #eff6ff;
      border-color: #1d4ed8;
    }
  }

  &.card-active {
    border: 2px solid #2563eb !important;
    background-color: #ffffff !important;
  }
}

.accessory-title {
  color: #0f172a;
  font-size: 0.88rem;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.accessory-subtitle {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Action Circle Buttons matching reference design */
.action-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &.circle-add {
    border: 2px solid #2563eb;
    background: #ffffff;
  }

  &.circle-selected {
    border: 2px solid #2563eb;
    background: #2563eb;
  }

  &.circle-locked {
    border: 2px solid #cbd5e1;
    background: #f1f5f9;
  }
}
.shadow-quote {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.04);
}
.inquiry-pill {
  background-color: #e2e8f0;
  letter-spacing: 0.05em;
  font-size: 0.72rem !important;
}
.safe-badge-container {
  background-color: #dcfce7;
}
.border-bottom-thin {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.acq-toggle, .term-toggle {
  border: 1px solid rgba(148, 163, 184, 0.2);
  width: 100%;
  background: white;
  height: 40px !important;
}
.acq-toggle button, .term-toggle button {
  flex-grow: 1;
  height: 40px !important;
}
.term-toggle button {
  font-size: 0.76rem !important;
  padding: 0 4px !important;
}
.acq-toggle :deep(.v-btn--selected), .term-toggle :deep(.v-btn--selected) {
  background-color: #1e293b !important;
  color: white !important;
}
.pricing-request-text {
  font-size: 2.15rem;
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.5px;
}
.price-val {
  letter-spacing: -1px;
  font-size: 50px;
}
.clip-text-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.accessories-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px; /* 18px gap (8px + 10px) on desktop */
}
.custom-outlined-dropdown {
  position: relative;
  width: 100%;
  user-select: none;
  z-index: 100;

  .dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 0 12px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 8px;
    background-color: white;
    font-size: 0.875rem;
    color: #1e293b;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
      border-color: rgba(0, 0, 0, 0.87);
    }
  }

  &.dropdown-open .dropdown-trigger,
  .dropdown-trigger:focus-within {
    border: 2px solid #1d4ed8;
    padding: 0 11px;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 5px 5px -3px rgba(0,0,0,0.2), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12);
    z-index: 110;
    max-height: 250px;
    overflow-y: auto;
    padding: 8px 0;
  }

  .dropdown-item {
    padding: 10px 16px;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &.active {
      background: rgba(26, 115, 232, 0.08);
      color: #1a73e8 !important;
      font-weight: 600;
    }
  }
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 959px) {
  .accessories-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px; /* 14px gap (4px + 10px) on mobile & tablet */
  }
  .addon-option-card {
    min-height: auto !important;
    padding: 8px !important;
  }
  /* Price scaling */
  .price-val {
    font-size: 32px !important;
  }
  /* Tighten acquisition card padding */
  .shadow-quote {
    padding: 16px !important;
  }
  /* General spacing cleanup */
  .mb-6 {
    margin-bottom: 12px !important;
  }
  /* Scale down features list text & spacing */
  .features-list :deep(.text-body-2) {
    font-size: 0.78rem !important;
  }
  .features-list :deep(.text-caption) {
    font-size: 0.7rem !important;
  }
  .features-list :deep(.py-2) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
  /* Scale down switches */
  .features-list :deep(.v-switch) {
    transform: scale(0.8) !important;
    transform-origin: right center;
  }
  /* Compact acquisition toggle */
  .acq-toggle {
    height: 38px !important;
  }
  .acq-toggle button {
    height: 100% !important;
    font-size: 0.8rem !important;
  }
  /* Hide active accessory badges */
  .active-accessory-badges {
    display: none !important;
  }
}

@media (max-width: 599px) {
  .accessories-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
/* Accessories sidebar blue glow blob */
.acc-blob-gradient {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.13) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

/* Specs Tabs & Header Subtitle Styles */
.product-title {
  @media (max-width: 600px) {
    font-size: 1.6rem !important;
    line-height: 1.25 !important;
  }
  @media (max-width: 420px) {
    font-size: 1.35rem !important;
    line-height: 1.2 !important;
  }
}

.product-subtitle {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
}

.product-subtitle span.dot {
  width: 4px;
  height: 4px;
  background-color: #cbd5e1;
  border-radius: 50%;
  display: inline-block;
}

.product-specs-tabs {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tabs-header {
  display: flex;
  gap: 6px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
}

.tab-btn {
  flex: 1;
  background: transparent;
  border: none;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(.active) {
    color: #0f172a;
    background: rgba(255, 255, 255, 0.6);
  }

  &.active {
    background: #ffffff;
    color: #2563eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.tab-content {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  animation: fadeInTab 0.25s ease-in-out;
}

@keyframes fadeInTab {
  from { opacity: 0; transform: translateY(3px); }
  to { opacity: 1; transform: translateY(0); }
}

.spec-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.825rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.spec-key {
  color: #64748b;
  font-weight: 500;
  flex: 0 0 45%;
}

.spec-val {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
  flex: 1;
}

.ai-highlights {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;

  :deep(ul) {
    margin: 0;
    padding-left: 18px;
    li {
      margin-bottom: 4px;
      line-height: 1.4;
      color: #334155;
      font-size: 0.8125rem;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.pricing-disclaimer-note {
  font-size: 10.5px;
  line-height: 1.35;
  color: #64748b;
}
</style>
